import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { API_BASE_URL } from '../tokens/api-base-url.token';
import { MeResponse } from '../models/auth.models';

/**
 * Single source of truth for "who is the logged-in user and what can they see".
 * MSAL handles the token round-trip; this service hits /api/auth/me once after
 * sign-in and exposes the result as signals so guards/components can react.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly msal = inject(MsalService);

  private readonly _me = signal<MeResponse | null>(null);
  private readonly _loading = signal<boolean>(false);

  readonly me = this._me.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly isAdmin = computed(() => this._me()?.isAdmin ?? false);
  readonly hasAnyAccess = computed(() => {
    const me = this._me();
    return me?.isAdmin || (me?.projects?.length ?? 0) > 0;
  });

  /** Returns the currently active MSAL account, or null if not signed in. */
  getActiveAccount() {
    const accounts = this.msal.instance.getAllAccounts();
    if (accounts.length === 0) {
      return null;
    }
    let active = this.msal.instance.getActiveAccount();
    if (!active) {
      active = accounts[0];
      this.msal.instance.setActiveAccount(active);
    }
    return active;
  }

  /**
   * Loads /api/auth/me and caches it. Idempotent — repeated calls inside the
   * same SPA session reuse the cached value unless force=true.
   */
  async loadMe(force = false): Promise<MeResponse | null> {
    if (!force && this._me()) {
      return this._me();
    }
    if (!this.getActiveAccount()) {
      return null;
    }
    this._loading.set(true);
    try {
      const me = await firstValueFrom(
        this.http.get<MeResponse>(`${this.apiBaseUrl}/api/auth/me`),
      );
      this._me.set(me);
      return me;
    } finally {
      this._loading.set(false);
    }
  }

  signOut(): void {
    this._me.set(null);
    void this.msal.logoutRedirect();
  }
}
